import React from "react";
import { FiUploadCloud } from "react-icons/fi";
import { ArrowLeft } from "lucide-react";

const ImportUI = ({
  heading = "Import",
  description = "Upload your file and continue with the import flow.",
  downloadHref,
  downloadName,
  downloadLabel = "Download Sample",
  onBack,
  inputRef,
  accept,
  onInputChange,
  showUploader = true,
  openFileDialog,
  isDragging = false,
  onDragOver,
  onDragLeave,
  onDrop,
  selectedFile,
  previewUrl = "",
  documentName = "",
  supportedTypes = [],
  onCancel,
  onNext,
  nextDisabled = false,
  nextLabel = "Next",
  cancelLabel = "Cancel",
  children,
}) => {
  return (
    <div className="flex w-full items-start justify-center bg-[#F8FBFA] px-3 py-3 sm:px-4 sm:py-4 lg:min-h-[calc(100vh-110px)] lg:px-0 lg:py-0">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col rounded-[24px] border border-[#D9E7E2] bg-white shadow-[0_18px_50px_rgba(6,81,61,0.08)] lg:min-h-[calc(100vh-110px)] lg:max-w-none lg:rounded-none lg:border-x-0 lg:border-b-0">
        <div className="border-b border-[#E6EEEC] px-4 py-4 sm:px-6 sm:py-5 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#06513D] bg-[#E6EEEC] text-[#06513D] transition hover:bg-[#dbe9e4] sm:mt-0 sm:h-11 sm:w-11"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="min-w-0">
                <h2 className="text-[22px] font-bold leading-tight text-[#06513D] sm:text-[24px]">
                  {heading}
                </h2>
                <p className="mt-1 max-w-[680px] text-sm leading-6 text-[#5F6D7A]">
                  {description}
                </p>
              </div>
            </div>

            {downloadHref ? (
              <a
                href={downloadHref}
                download={downloadName}
                className="shrink-0 self-start text-sm font-semibold text-[#06513D] underline underline-offset-4 hover:text-[#04392b] sm:self-center"
              >
                {downloadLabel}
              </a>
            ) : null}
          </div>
        </div>

        <div className="px-4 py-4 sm:px-6 sm:py-5 lg:flex lg:flex-1 lg:flex-col lg:px-8">
          {inputRef ? (
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={onInputChange}
              className="hidden"
            />
          ) : null}

          {showUploader ? (
            <div className="flex flex-col items-center justify-start lg:flex-1">
              <div className="flex w-full items-start justify-center lg:flex-1 lg:items-center">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={openFileDialog}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openFileDialog?.();
                    }
                  }}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  className={`flex min-h-[260px] w-full max-w-[720px] flex-col items-center justify-center rounded-[24px] border-2 border-dashed px-5 py-7 text-center transition-all sm:min-h-[290px] sm:px-8 sm:py-8 lg:min-h-[420px] lg:max-w-[920px] lg:px-12 lg:py-10 ${
                    isDragging
                      ? "border-[#06513D] bg-[#EFF7F4]"
                      : "border-[#06513D] bg-[#FCFEFD]"
                  }`}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#EAF3F0]">
                    <FiUploadCloud className="h-7 w-7 text-[#3D4B58]" />
                  </div>

                  <h3 className="mt-4 text-[20px] font-bold leading-tight text-[#293241] sm:text-[22px]">
                    Select Document to Upload
                  </h3>

                  {!selectedFile ? (
                    <>
                      <p className="mt-3 max-w-[420px] text-[14px] leading-6 text-[#98A2B3] sm:text-[15px]">
                        Drag and drop your file or{" "}
                        <span className="font-semibold text-[#06513D] underline underline-offset-4">
                          browse
                        </span>
                      </p>

                      <p className="mt-3 text-xs font-medium text-[#98A2B3]">
                        Supported formats: {supportedTypes.join(", ")}
                      </p>
                    </>
                  ) : (
                    <div className="flex w-full max-w-[520px] flex-col items-center">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={selectedFile.name}
                          className="mb-4 h-32 w-full rounded-2xl border border-[#DDE8E4] object-cover shadow-sm"
                        />
                      ) : null}

                      <div className="mt-4 w-full text-center">
                        <p className="break-words text-[17px] font-bold text-[#293241] sm:text-[18px]">
                          {documentName || selectedFile.name}
                        </p>
                        <p className="mt-2 text-[13px] leading-5 text-[#98A2B3]">
                          Click this area to choose another file.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex w-full flex-col-reverse items-stretch justify-center gap-3 border-t border-[#EFF3F1] pt-4 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={onCancel}
                  className="min-w-[132px] hover:bg-[#F8F8F8] bg-[#E6EDEB] rounded-md border border-[#06513D] px-5 py-2.5 font-semibold text-[#06513D] sm:w-auto"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={nextDisabled}
                  className={`min-w-[132px] rounded-md px-5 py-2.5 font-semibold text-white transition sm:w-auto ${
                    nextDisabled
                      ? "cursor-not-allowed bg-[#9AA6B2]"
                      : " bg-[#06513D] hover:!bg-[#387464]"
                  }`}
                >
                  {nextLabel}
                </button>
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportUI;
